import { useEffect, useReducer } from 'react';
import { X } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useContextHook } from 'use-context-hook';
import { AuthContext } from '../../Context/authContext';
import cronJobsService from '../../services/cronJobsService';
import { getAtDateAndTime, getEveryValueAndUnit } from '../../helpers/common';
import { queryKeys } from '../../hooks/queryKeys';
import Toast from '../Toast';

const actionOptions = [
  { label: 'Notification', value: 'notification' },
  { label: 'Task', value: 'task' },
];

const channelOptions = [
  { label: 'Email', value: 'email' },
  { label: 'In-App', value: 'app' },
];

const unitOptions = [
  { label: 'Minutes', value: 'minutes' },
  { label: 'Hours', value: 'hours' },
  { label: 'Days', value: 'days' },
];

const scheduleTypeOptions = [
  { label: 'Every', value: 'every' },
  { label: 'At', value: 'at' },
  { label: 'After', value: 'after' },
];

const inputClass =
  'w-full bg-white border border-shell-card-border rounded-lg px-3 py-2 text-sm text-ink placeholder:text-muted-faint outline-none focus:border-primary/40';

const selectClass = `${inputClass} appearance-none`;

const initialFormState = {
  name: '',
  description: '',
  isEnabled: false,
  scheduleType: 'every',
  every: '',
  unit: 'minutes',
  runAt: '',
  time: '',
  action: '',
  channel: '',
  emailInput: '',
  emails: [],
  isLoading: false,
};

function formFromInitialData(initialData) {
  if (!initialData) return initialFormState;

  const scheduleType = initialData?.schedule?.kind || initialData?.schedule_type || 'every';
  const everyMeta = getEveryValueAndUnit(initialData?.schedule?.everyMs);
  const atMeta = getAtDateAndTime(initialData?.schedule?.atMs);

  return {
    ...initialFormState,
    name: initialData?.name || '',
    description: initialData?.payload?.message || initialData?.message || '',
    isEnabled: initialData?.enabled ?? initialData?.enable ?? false,
    scheduleType,
    every: everyMeta?.value != null ? String(everyMeta.value) : '',
    unit: everyMeta?.unit || 'minutes',
    runAt: atMeta?.date || '',
    time: atMeta?.time || '',
    action: initialData?.action?.action_type || initialData?.action?.actionType || '',
    channel: initialData?.action?.channel || '',
    emails: initialData?.emails || [],
  };
}

function formReducer(state, action) {
  switch (action.type) {
    case 'SET_FIELD':
      return { ...state, [action.field]: action.value };
    case 'TOGGLE_ENABLED':
      return { ...state, isEnabled: !state.isEnabled };
    case 'ADD_EMAIL': {
      const email = action.email.trim();
      if (!email || state.emails.includes(email)) {
        return { ...state, emailInput: '' };
      }
      return { ...state, emails: [...state.emails, email], emailInput: '' };
    }
    case 'REMOVE_EMAIL':
      return { ...state, emails: state.emails.filter((_, index) => index !== action.index) };
    case 'RESET':
      return formFromInitialData(action.initialData);
    case 'SET_LOADING':
      return { ...state, isLoading: action.value };
    default:
      return state;
  }
}

function FieldLabel({ children, required }) {
  return (
    <label className="text-sm text-muted mb-1.5 block">
      {required && <span className="text-red-500">*</span>} {children}
    </label>
  );
}

function buildScheduleValue({ scheduleType, every, unit, runAt, time }) {
  if (scheduleType === 'every' || scheduleType === 'after') {
    const everyValue = parseInt(every, 10) || 0;
    const unitMultipliers = {
      minutes: 60,
      hours: 3600,
      days: 86400,
    };
    return String(everyValue * (unitMultipliers[unit] || 1));
  }

  if (scheduleType === 'at' && runAt && time) {
    const dateObj = new Date(`${runAt}T${time}`);
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
      .format(dateObj)
      .replace(/,/g, '');
  }

  return '';
}

/**
 * Create / Edit Task modal.
 * API / payload unchanged:
 *  - Create → POST /cronjob/add
 *  - Update → PUT  /cronjob/update
 *
 * Payload fields: user_id, enable, name, schedule_type, value, message,
 *                 platform_id, action { action_type, channel }, emails, [job_id]
 */
export function CronJobForm({ onCancel, initialData, setShowForm }) {
  const queryClient = useQueryClient();
  const { user, refetch } = useContextHook(AuthContext, v => ({
    user: v.user,
    refetch: v.refetch,
  }));

  const [form, dispatch] = useReducer(formReducer, initialData, formFromInitialData);

  const {
    name,
    description,
    isEnabled,
    scheduleType,
    every,
    unit,
    runAt,
    time,
    action,
    channel,
    emailInput,
    emails,
    isLoading,
  } = form;

  useEffect(() => {
    dispatch({ type: 'RESET', initialData });
  }, [initialData]);

  const setField = field => e => {
    dispatch({ type: 'SET_FIELD', field, value: e.target.value });
  };

  const handleEmailKeyDown = e => {
    if (e.key !== 'Enter') return;
    e.preventDefault();

    const value = emailInput.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!value || !emailRegex.test(value)) return;

    dispatch({ type: 'ADD_EMAIL', email: value });
  };

  const canSubmit =
    name.trim() &&
    description.trim() &&
    scheduleType &&
    action &&
    channel &&
    (scheduleType === 'at' ? runAt && time : every) &&
    !isLoading;

  const handleSubmit = async e => {
    e.preventDefault();
    if (!canSubmit) return;

    dispatch({ type: 'SET_LOADING', value: true });

    const payload = {
      user_id: user?.email,
      name: name.trim(),
      schedule_type: scheduleType,
      value: buildScheduleValue(form),
      message: description.trim(),
      enable: isEnabled,
      platform_id: 'plateslabs',
      emails,
      action: {
        action_type: action,
        channel,
      },
    };

    try {
      if (initialData) {
        await cronJobsService.updateJob({
          ...payload,
          job_id: initialData?.id || initialData?.job_id,
        });
        Toast({ type: 'success', message: 'Task Updated Successfully' });
      } else {
        await cronJobsService.createJob(payload);
        Toast({ type: 'success', message: 'Task Added Successfully' });
      }

      queryClient.invalidateQueries({ queryKey: queryKeys.cronJobs.all });
      refetch?.();
      setShowForm?.(false);
      onCancel?.();
    } catch (error) {
      Toast({ type: 'error', message: error?.detail || error?.message || 'Something went wrong!!' });
    } finally {
      dispatch({ type: 'SET_LOADING', value: false });
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onCancel}>
      <div
        className="bg-shell-card border border-shell-card-border rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-1">
          <div>
            <h3 className="text-lg font-semibold text-ink">{initialData ? 'Edit Task' : 'New Task'}</h3>
            <p className="text-sm text-muted mt-1">
              Schedule a recurring or one-time task. Functionality and API parameters stay the same.
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="p-1.5 text-muted hover:text-ink rounded-lg"
            aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="mt-5 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-4 items-end">
            <div>
              <FieldLabel required>Name</FieldLabel>
              <input value={name} onChange={setField('name')} placeholder="Task name" className={inputClass} />
            </div>
            <div>
              <FieldLabel required>Description</FieldLabel>
              <input
                value={description}
                onChange={setField('description')}
                placeholder="What does this job do?"
                className={inputClass}
              />
            </div>
            <div className="flex items-center gap-2 pb-2">
              <button
                type="button"
                role="switch"
                aria-checked={isEnabled}
                onClick={() => dispatch({ type: 'TOGGLE_ENABLED' })}
                className={`relative w-11 h-6 rounded-full transition-colors ${
                  isEnabled ? 'bg-primary' : 'bg-[#d4d4d4]'
                }`}>
                <span
                  className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                    isEnabled ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
              <span className="text-xs text-muted">{isEnabled ? 'Enabled' : 'Disabled'}</span>
            </div>
          </div>

          <div>
            <FieldLabel required>Schedule Type</FieldLabel>
            <select value={scheduleType} onChange={setField('scheduleType')} className={selectClass}>
              {scheduleTypeOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {(scheduleType === 'every' || scheduleType === 'after') && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <FieldLabel required>Every</FieldLabel>
                <input
                  type="number"
                  min="0"
                  value={every}
                  onChange={setField('every')}
                  placeholder="0"
                  className={inputClass}
                />
              </div>
              <div>
                <FieldLabel required>Unit</FieldLabel>
                <select value={unit} onChange={setField('unit')} className={selectClass}>
                  {unitOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {scheduleType === 'at' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <FieldLabel required>Run At (Date)</FieldLabel>
                <input
                  type="date"
                  value={runAt}
                  onChange={setField('runAt')}
                  className={`${inputClass} [color-scheme:light]`}
                />
              </div>
              <div>
                <FieldLabel required>Time</FieldLabel>
                <input
                  type="time"
                  value={time}
                  onChange={setField('time')}
                  className={`${inputClass} [color-scheme:light]`}
                />
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <FieldLabel required>Action</FieldLabel>
              <select value={action} onChange={setField('action')} className={selectClass}>
                <option value="">Select any action</option>
                {actionOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <FieldLabel required>Channel</FieldLabel>
              <select value={channel} onChange={setField('channel')} className={selectClass}>
                <option value="">Select any channel</option>
                {channelOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {channel === 'email' && (
            <div>
              <FieldLabel>Email Address</FieldLabel>
              <input
                type="email"
                value={emailInput}
                onChange={setField('emailInput')}
                onKeyDown={handleEmailKeyDown}
                autoComplete="new-password"
                placeholder="recipient@example.com (press Enter to add)"
                className={inputClass}
              />
              {emails.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {emails.map((email, index) => (
                    <div
                      key={`${email}-${index}`}
                      className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/15 border border-primary/30 text-xs text-ink">
                      <span>{email}</span>
                      <button
                        type="button"
                        onClick={() => dispatch({ type: 'REMOVE_EMAIL', index })}
                        className="text-muted hover:text-ink"
                        aria-label={`Remove ${email}`}>
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-muted hover:text-ink">
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="px-4 py-2 text-sm font-medium bg-primary hover:bg-primary/90 text-white rounded-lg disabled:opacity-50">
              {isLoading
                ? initialData
                  ? 'Updating...'
                  : 'Adding...'
                : initialData
                  ? 'Update Task'
                  : 'Add Task'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
